import { useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useCampusAuth } from "@/contexts/CampusAuth";

const inputCls =
  "w-full bg-transparent border-0 border-b border-sand-800/25 focus:border-terra rounded-none px-0 h-11 text-[15.5px] text-sand-800 outline-none transition-colors placeholder:text-sand-400";

export default function CampusLogin() {
  const { user, login } = useCampusAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  if (user) {
    const to = location.state?.from?.pathname || "/campus/app";
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    const res = await login({ email: form.email, password: form.password });
    setBusy(false);
    if (res.ok) {
      const to = location.state?.from?.pathname || "/campus/app";
      navigate(to, { replace: true });
    } else {
      setErr(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ backgroundColor: "#F6E8DC" }}>
      {/* LEFT · Full-bleed editorial photo */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="/images/brand/atlas-lesen-fenster.jpg"
          alt="Pflegekraft liest das Dickies-Campus Journal am Fenster"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-espresso-deep/70 via-espresso-deep/10 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12 xl:p-16 max-w-xl">
          <div className="font-poster uppercase tracking-[0.35em] text-cream-50/80 text-[11px]">
            Akademie für Pflege · Campus · MMXXVI
          </div>
          <h1 className="mt-4 font-magazine text-cream-50 leading-[1.1] text-[2.6rem] xl:text-[3.2rem] font-medium tracking-tight">
            Dein Wissen wächst —<br />
            <span className="italic">jeden Tag ein Kapitel.</span>
          </h1>
          <div className="mt-6 h-px w-14 bg-terra" />
          <p className="mt-4 font-magazine italic text-cream-50/85 text-[15.5px]">
            Über 1.400 Pflegekräfte lernen bereits hier.
          </p>
        </div>
      </div>

      {/* RIGHT · Form column */}
      <div className="flex flex-col items-center justify-center px-6 py-14 sm:px-12">
        <Link to="/" className="flex flex-col items-center gap-3 mb-10" data-testid="campus-login-home-link">
          <img src="/dh_logo_dark.webp" alt="Dickies-Campus" className="h-12 w-auto" />
          <span className="font-magazine text-[1.9rem] text-sand-800 leading-none">
            Dickies-<span className="italic text-terra">Campus</span>
          </span>
        </Link>

        <div className="w-full max-w-md bg-cream-50 border border-sand-800/15 shadow-[0_24px_60px_-30px_rgba(82,42,37,0.35)] p-2">
          <div className="border border-sand-800/10 px-8 py-10 sm:px-10">
            <div className="font-poster uppercase tracking-[0.3em] text-terra text-[11px] font-medium">
              Willkommen zurück
            </div>
            <h2 className="mt-2 font-magazine text-[1.9rem] sm:text-[2.2rem] text-sand-800 leading-tight tracking-tight">
              Schön, dass du <span className="italic text-terra">da bist</span>.
            </h2>

            <form onSubmit={submit} className="mt-9 grid gap-7" data-testid="campus-login-form">
              <div>
                <label className="font-poster text-[10px] uppercase tracking-[0.28em] font-medium text-sand-600 mb-1 block">
                  E-Mail
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="campus-login-email"
                  className={inputCls}
                />
              </div>

              <div>
                <label className="font-poster text-[10px] uppercase tracking-[0.28em] font-medium text-sand-600 mb-1 block">
                  Passwort
                </label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    required
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="campus-login-password"
                    className={`${inputCls} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    data-testid="campus-login-toggle-pw"
                    className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center text-sand-500 hover:text-sand-800"
                    aria-label="Passwort anzeigen"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" strokeWidth={1.6} /> : <Eye className="h-4 w-4" strokeWidth={1.6} />}
                  </button>
                </div>
                <div className="text-right mt-2">
                  <Link
                    to="/forgot-password"
                    data-testid="campus-login-forgot"
                    className="text-[12px] text-sand-600 hover:text-terra underline-offset-4 hover:underline"
                  >
                    Passwort vergessen?
                  </Link>
                </div>
              </div>

              {err && (
                <p className="text-[13px] text-terra font-medium -mt-3" data-testid="campus-login-error">
                  {err}
                </p>
              )}

              <button
                type="submit"
                disabled={busy}
                data-testid="campus-login-submit"
                className="mx-auto mt-1 font-poster uppercase tracking-[0.28em] text-[13px] font-medium text-terra border border-terra rounded-full h-12 px-12 hover:bg-terra hover:text-cream-50 transition-colors disabled:opacity-60"
              >
                {busy ? "Anmelden…" : "Anmelden"}
              </button>

              <p className="font-magazine italic text-sand-600 text-[14.5px] text-center leading-snug">
                Probleme beim Login? Sprich deine Kursleitung an.
              </p>

              <p className="text-[12px] text-sand-500 text-center -mt-3">
                Noch kein Zugang?{" "}
                <Link
                  to="/preise"
                  data-testid="campus-auth-link-preise"
                  className="text-sand-800 underline-offset-4 hover:underline font-medium"
                >
                  Pakete &amp; Preise ansehen →
                </Link>
              </p>
            </form>
          </div>
        </div>

        <div className="mt-8 text-[11.5px] text-sand-500 text-center">
          Jeden <span className="text-terra font-medium">Montag</span> startklar · Support Mo–Fr 8–18 Uhr ·{" "}
          <Link to="/" className="underline-offset-4 hover:underline">Barrierefreiheit</Link>
        </div>
      </div>
    </div>
  );
}
